import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Strengthening Social Bonds',
    content: [
      '<strong>Learning Goal:</strong> Practice communication and empathy skills, identify relational barriers, and develop strategies for nurturing social connections.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The psychology of trust and empathy',
      '• Effective communication skills',
      '• Overcoming relationship barriers',
      '• Building new connections',
      '<br/>',
      '<strong>Discussion:</strong> Strong relationships don\'t happen by accident–they require intentional effort, skill development, and consistent nurturing. This module provides practical tools for building healthier social bonds.'
    ]
  },
  {
    title: 'The Psychology of Trust and Empathy',
    content: [
      'Trust and empathy form the foundation of strong relationships. Understanding these concepts helps you build deeper connections.',
      '<br/>',
      '<strong>Building Trust:</strong>',
      '• <strong>Consistency:</strong> Keep promises and follow through',
      '• <strong>Reliability:</strong> Be there when you say you will be',
      '• <strong>Honesty:</strong> Tell the truth, even when it\'s difficult',
      '• <strong>Vulnerability:</strong> Share your authentic self gradually',
      '<br/>',
      '<strong>Developing Empathy:</strong>',
      '• <strong>Cognitive Empathy:</strong> Understanding another\'s perspective',
      '• <strong>Emotional Empathy:</strong> Feeling what another person feels',
      '• <strong>Compassionate Empathy:</strong> Taking action to help',
      '<br/>',
      '<strong>Trust-Empathy Cycle:</strong>',
      '• Empathy builds trust',
      '• Trust enables deeper empathy',
      '• Together they create strong, resilient relationships'
    ]
  },
  {
    title: 'Active Listening Skills',
    content: [
      'Active listening is a cornerstone of effective communication and relationship building.',
      '<br/>',
      '<strong>Key Components of Active Listening:</strong>',
      '• <strong>Pay Attention:</strong> Give the speaker your full focus',
      '• <strong>Show You\'re Listening:</strong> Use nonverbal cues (nodding, eye contact)',
      '• <strong>Provide Feedback:</strong> Reflect back what you heard',
      '• <strong>Defer Judgment:</strong> Don\'t interrupt or criticize',
      '• <strong>Respond Appropriately:</strong> Ask clarifying questions',
      '<br/>',
      '<strong>Reflective Statements:</strong>',
      '• "What I hear you saying is..."',
      '• "It sounds like you\'re feeling..."',
      '• "You seem concerned about..."',
      '<br/>',
      '<strong>Benefits:</strong>',
      '• Makes others feel valued and understood',
      '• Reduces misunderstandings',
      '• Builds trust and intimacy',
      '• Helps resolve conflicts more effectively'
    ]
  },
  {
    title: 'Effective Communication Techniques',
    content: [
      'Good communication involves both expressing yourself clearly and understanding others.',
      '<br/>',
      '<strong>"I" Statements:</strong>',
      '• Instead of: "You never listen to me"',
      '• Try: "I feel unheard when conversations get interrupted"',
      '• Focus on your feelings and needs, not blaming others',
      '<br/>',
      '<strong>Clear Requests:</strong>',
      '• Be specific about what you want',
      '• "Could we talk about this when you\'re not busy?"',
      '• "I\'d like more quality time together"',
      '<br/>',
      '<strong>Expressing Appreciation:</strong>',
      '• "I really appreciate how you..."',
      '• "Thank you for..."',
      '• "I\'m grateful that..."',
      '• Regular positive feedback strengthens relationships',
      '<br/>',
      '<strong>Timing Matters:</strong>',
      '• Choose appropriate times for important conversations',
      '• Avoid discussing sensitive topics when stressed or tired',
      '• Schedule regular check-ins for ongoing relationships'
    ]
  },
  {
    title: 'Overcoming Relationship Barriers',
    content: [
      'All relationships face challenges. Learning to identify and overcome barriers is key to maintaining strong connections.',
      '<br/>',
      '<strong>Common Barriers:</strong>',
      '• <strong>Distance:</strong> Physical separation or busy schedules',
      '• <strong>Misunderstandings:</strong> Poor communication or assumptions',
      '• <strong>Conflict:</strong> Unresolved disagreements or hurt feelings',
      '• <strong>Life Changes:</strong> Major transitions (jobs, moves, family changes)',
      '<br/>',
      '<strong>Strategies to Overcome:</strong>',
      '• <strong>For Distance:</strong> Regular video calls, written notes, planned visits',
      '• <strong>For Misunderstandings:</strong> Clarify intentions, ask questions',
      '• <strong>For Conflict:</strong> Use "I" statements, focus on solutions',
      '• <strong>For Life Changes:</strong> Communicate openly about needs and support',
      '<br/>',
      '<strong>Growth Mindset:</strong>',
      '• View challenges as opportunities to strengthen relationships',
      '• Learn from conflicts rather than avoiding them',
      '• Be willing to compromise and adapt'
    ]
  },
  {
    title: 'Building New Connections',
    content: [
      'Expanding your social network requires intentional effort and stepping outside your comfort zone.',
      '<br/>',
      '<strong>Finding New Connections:</strong>',
      '• <strong>Join Groups:</strong> Clubs, classes, sports teams, hobby groups',
      '• <strong>Volunteer:</strong> Community service, charity work, local events',
      '• <strong>Work/School:</strong> Colleagues, classmates, professional networks',
      '• <strong>Online Communities:</strong> Interest-based forums, social media groups',
      '<br/>',
      '<strong>Making Initial Contact:</strong>',
      '• Start with shared interests or activities',
      '• Ask open-ended questions',
      '• Listen more than you talk initially',
      '• Follow up on conversations',
      '<br/>',
      '<strong>Digital Age Connections:</strong>',
      '• Use social media mindfully for relationship building',
      '• Balance online and in-person interactions',
      '• Be authentic in digital communications',
      '• Use technology to maintain long-distance relationships',
      '<br/>',
      '<strong>Quality Over Quantity:</strong>',
      '• Focus on a few meaningful connections rather than many superficial ones',
      '• Invest time in relationships that align with your values',
      '• Be patient–strong relationships take time to develop'
    ]
  },
  {
    title: 'Empathy Workshop Activity',
    content: [
      '<strong>Practice Session:</strong> This activity helps you develop empathy and active listening skills.',
      '<br/>',
      '<strong>Exercise 1: Reflective Listening Pairs</strong>',
      '• Pair up with someone (or imagine a conversation)',
      '• Person A shares: "Tell me about a time you felt really supported by someone"',
      '• Person B listens actively and responds only with reflective statements:',
      '• "What I hear you saying is..."',
      '• "It sounds like that made you feel..."',
      '• "You seem to appreciate..."',
      '• Switch roles after 3-5 minutes',
      '<br/>',
      '<strong>Exercise 2: Empathy Challenge</strong>',
      '• Think of someone you know who is going through a difficult time',
      '• Practice putting yourself in their shoes',
      '• Write down what they might be feeling and thinking',
      '• Consider what kind of support would be most helpful',
      '<br/>',
      '<strong>Exercise 3: Gratitude Practice</strong>',
      '• Identify three people who have supported you recently',
      '• Write a short note expressing specific appreciation',
      '• (Optional) Share the note with them',
      '<br/>',
      '<strong>Takeaway:</strong> These exercises build the skills needed for stronger, more empathetic relationships. Practice regularly to see improvements in your social connections.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 4: Strengthening Social Bonds! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about communication and relationship-building skills.',
        'The quiz will help you review key concepts about developing stronger social connections.'
      ]
    }
  }
];

const SocioRelationalModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('socioRelationalM4');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/socio-relational-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/socio-relational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left socio-relational-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Strengthening Social Bonds</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn effective communication strategies, practice active listening, and develop skills for building deeper connections.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/socio-relational-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule4Page;

